import { useMemo, useState } from 'react'
import { Button, Card, Descriptions, Drawer, Empty, Input, Message, Select, Spin } from '@arco-design/web-react'
import { IconRefresh, IconSearch } from '@arco-design/web-react/icon'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Copy } from 'lucide-react'
import { getPricingApi, type PricingEntry } from '@/api/pricing'
import { VendorIcon } from '@/components/Common/VendorIcon'
import { useStatusStore } from '@/stores/statusStore'
import {
  Pill,
  normalizeModelFeatureTag,
  normalizeModelFeatureTagKey,
  renderGroupTag,
  renderModelFeatureTags,
  splitModelFeatureTags,
} from '@/utils/render'
import {
  calculateModelPrice,
  normalizePricingResponse,
  type PricingDisplayType,
} from '@/utils/modelPricing'
import { useBreakpoint } from '@/hooks/useBreakpoint'

const ALL = 'all'

function includesKeyword(model: PricingEntry, keyword: string) {
  if (!keyword) return true
  const value = keyword.toLowerCase()
  return [
    model.model_name,
    model.description,
    model.tags,
    model.vendor_name,
  ].some((item) => item?.toLowerCase().includes(value))
}

function endpointLabel(endpoint: string, endpointMap: Record<string, { name?: string }>) {
  return endpointMap[endpoint]?.name || endpoint
}

function vendorKey(model: PricingEntry, fallback: string) {
  return model.vendor_name || fallback
}

export default function AvailableModels() {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()
  const status = useStatusStore((s) => s.status)
  const [keyword, setKeyword] = useState('')
  const [vendor, setVendor] = useState(ALL)
  const [tag, setTag] = useState(ALL)
  const [quotaType, setQuotaType] = useState(ALL)
  const [endpoint, setEndpoint] = useState(ALL)
  const [group, setGroup] = useState(ALL)
  const [detail, setDetail] = useState<PricingEntry | null>(null)

  const pricingQuery = useQuery({
    queryKey: ['available-models-pricing'],
    queryFn: getPricingApi,
  })

  const normalized = useMemo(() => normalizePricingResponse(pricingQuery.data), [pricingQuery.data])

  const displayType = (status?.quota_display_type ||
    (status?.display_in_currency === false ? 'TOKENS' : 'USD')) as PricingDisplayType
  const priceContext = useMemo(
    () => ({
      displayType,
      currency: displayType === 'TOKENS' ? 'USD' : displayType,
      priceRate: status?.price ?? 1,
      usdExchangeRate: status?.usd_exchange_rate ?? status?.price ?? 1,
      customExchangeRate: status?.custom_currency_exchange_rate ?? 1,
      customCurrencySymbol: status?.custom_currency_symbol ?? '¤',
      showWithRecharge: false,
      tokenUnit: 'M' as const,
    }),
    [displayType, status]
  )

  const tagOptions = useMemo(() => {
    const tags = new Map<string, string>()
    for (const model of normalized.models) {
      splitModelFeatureTags(model.tags).forEach((item) => {
        const key = normalizeModelFeatureTagKey(item)
        if (!tags.has(key)) tags.set(key, normalizeModelFeatureTag(item))
      })
    }
    return Array.from(tags.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [normalized.models])

  const filteredModels = useMemo(() => {
    return normalized.models.filter((model) => {
      if (!includesKeyword(model, keyword.trim())) return false
      if (vendor !== ALL && (model.vendor_name || 'unknown') !== vendor) return false
      if (tag !== ALL && !splitModelFeatureTags(model.tags).some((item) => normalizeModelFeatureTagKey(item) === tag)) return false
      if (quotaType !== ALL && String(model.quota_type) !== quotaType) return false
      if (endpoint !== ALL && !(model.supported_endpoint_types || []).includes(endpoint)) return false
      if (group !== ALL && !(model.enable_groups || []).includes(group)) return false
      return true
    })
  }, [endpoint, group, keyword, normalized.models, quotaType, tag, vendor])

  const groupedModels = useMemo(() => {
    const fallback = t('tt.models.unknownVendor')
    const groups = new Map<string, PricingEntry[]>()
    for (const model of filteredModels) {
      const key = vendorKey(model, fallback)
      groups.set(key, [...(groups.get(key) || []), model])
    }
    return Array.from(groups.entries())
      .map(([name, models]) => {
        const iconModel = models.find((model) => model.vendor_icon || model.icon)
        return {
          name,
          icon: iconModel?.vendor_icon || iconModel?.icon,
          iconLabel: iconModel?.vendor_icon || iconModel?.icon,
          description: models.find((model) => model.vendor_description)?.vendor_description,
          models,
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [filteredModels, t])

  const copyModelName = async (name: string) => {
    await navigator.clipboard.writeText(name)
    Message.success(t('tt.models.copiedName'))
  }

  if (status?.pricing === false) {
    return (
      <div className="space-y-4">
        <Card className="!rounded-2xl !border-[#E2E8F0] !shadow-card">
          <Empty description={t('tt.models.closedEmpty')} />
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="!rounded-2xl !border-[#E2E8F0] !shadow-card">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              placeholder={t('tt.models.searchPlaceholder')}
              value={keyword}
              onChange={setKeyword}
              prefix={<IconSearch />}
              allowClear
              className="w-full sm:w-[260px]"
            />
            <Select placeholder={t('tt.models.allVendors')} value={vendor} onChange={(value) => setVendor(value || ALL)} className="w-full sm:w-[170px]" allowClear>
              <Select.Option value={ALL}>{t('tt.models.allVendors')}</Select.Option>
              <Select.Option value="unknown">{t('tt.models.unknownVendor')}</Select.Option>
              {Array.from(new Set(normalized.models.map((model) => model.vendor_name).filter(Boolean))).map((name) => (
                <Select.Option key={name} value={name as string}>
                  {name}
                </Select.Option>
              ))}
            </Select>
            <Select placeholder={t('tt.models.tagFilter')} value={tag} onChange={(value) => setTag(value || ALL)} className="w-full sm:w-[150px]" allowClear>
              <Select.Option value={ALL}>{t('tt.models.allTags')}</Select.Option>
              {tagOptions.map((item) => (
                <Select.Option key={item.value} value={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
            <Select placeholder={t('tt.models.billingFilter')} value={quotaType} onChange={(value) => setQuotaType(value || ALL)} className="w-full sm:w-[130px]" allowClear>
              <Select.Option value={ALL}>{t('tt.models.allBilling')}</Select.Option>
              <Select.Option value="0">{t('tt.models.perToken')}</Select.Option>
              <Select.Option value="1">{t('tt.models.perUse')}</Select.Option>
            </Select>
            <Select placeholder={t('tt.models.endpointFilter')} value={endpoint} onChange={(value) => setEndpoint(value || ALL)} className="w-full sm:w-[160px]" allowClear>
              <Select.Option value={ALL}>{t('tt.models.allEndpoints')}</Select.Option>
              {normalized.supportedEndpointOptions.map((item) => (
                <Select.Option key={item} value={item}>
                  {endpointLabel(item, normalized.supportedEndpointMap)}
                </Select.Option>
              ))}
            </Select>
            <Select placeholder={t('tt.models.groupFilter')} value={group} onChange={(value) => setGroup(value || ALL)} className="w-full sm:w-[145px]" allowClear>
              <Select.Option value={ALL}>{t('tt.models.bestGroup')}</Select.Option>
              {Object.keys(normalized.usableGroup).map((item) => (
                <Select.Option key={item} value={item}>
                  {normalized.usableGroup[item] || item}
                </Select.Option>
              ))}
            </Select>
          </div>
          <Button className="w-full sm:w-auto" icon={<IconRefresh />} loading={pricingQuery.isFetching} onClick={() => pricingQuery.refetch()}>
            {t('tt.models.refresh')}
          </Button>
        </div>
      </Card>

      <Card className="!rounded-2xl !border-[#E2E8F0] !shadow-card">
        {pricingQuery.isLoading ? (
          <div className="flex justify-center py-20">
            <Spin />
          </div>
        ) : filteredModels.length === 0 ? (
          <Empty description={t('tt.models.noMatch')} />
        ) : (
          <div className="space-y-5">
            {groupedModels.map((vendorGroup) => (
              <section key={vendorGroup.name} className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#F1F5F9] pb-2">
                  <div className="flex min-w-0 items-center gap-3">
                    <VendorIcon name={vendorGroup.name} icon={vendorGroup.icon} size={36} />
                    <div className="min-w-0">
                      <div className="truncate text-[15px] font-semibold text-[#0F172A]">{vendorGroup.name}</div>
                      {vendorGroup.description && (
                        <div className="truncate text-xs text-[#6B7280]">{vendorGroup.description}</div>
                      )}
                    </div>
                  </div>
                  <Pill variant="neutral">{vendorGroup.models.length}</Pill>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3">
                  {vendorGroup.models.map((model) => {
                    const price = calculateModelPrice(model, group, normalized.groupRatio, priceContext)
                    return (
                      <div
                        key={model.model_name}
                        role="button"
                        tabIndex={0}
                        className="group flex min-h-[142px] flex-col rounded-xl border border-[#E2E8F0] bg-white p-3 text-left transition hover:-translate-y-0.5 hover:border-[#C7D2FE] hover:shadow-[0_10px_28px_rgba(79,70,229,0.10)]"
                        onClick={() => setDetail(model)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            setDetail(model)
                          }
                        }}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-[#0F172A]" title={model.model_name}>
                              {model.model_name}
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-1">
                              <Pill variant={model.quota_type === 1 ? 'success' : 'violet'}>
                                {model.quota_type === 1 ? t('tt.models.perUse') : t('tt.models.perToken')}
                              </Pill>
                              {renderModelFeatureTags(model.tags, 2)}
                            </div>
                          </div>
                          <Button
                            size="mini"
                            type="text"
                            icon={<Copy size={14} />}
                            aria-label={t('tt.models.copyName')}
                            onClick={(event) => {
                              event.stopPropagation()
                              copyModelName(model.model_name)
                            }}
                          />
                        </div>
                        <div className="mt-3 rounded-lg bg-[#F8FAFF] px-3 py-2">
                          <div className="truncate text-[13px] font-semibold text-[#111827]" title={price.primary}>
                            {price.primary}
                          </div>
                          {price.secondary && <div className="mt-0.5 truncate text-xs text-[#6B7280]">{price.secondary}</div>}
                        </div>
                        <div className="mt-auto flex flex-wrap gap-1 pt-3">
                          {(model.supported_endpoint_types || []).slice(0, 2).map((item) => (
                            <Pill key={item} variant="neutral">
                              {endpointLabel(item, normalized.supportedEndpointMap)}
                            </Pill>
                          ))}
                          {(model.supported_endpoint_types || []).length > 2 && (
                            <Pill variant="neutral">+{(model.supported_endpoint_types || []).length - 2}</Pill>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </Card>

      <Drawer
        visible={!!detail}
        onCancel={() => setDetail(null)}
        title={t('tt.models.detail')}
        width={isMobile ? '100%' : 560}
        footer={null}
      >
        {detail && (() => {
          const price = calculateModelPrice(detail, group, normalized.groupRatio, priceContext)
          return (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">{detail.model_name}</h3>
                <p className="mt-1 text-sm leading-relaxed text-[#6B7280]">{detail.description || t('tt.models.noDescription')}</p>
              </div>
              <Card className="!rounded-2xl">
                <div className="space-y-2">
                  {price.items.map((item) => (
                    <div key={item.key} className="flex items-center justify-between gap-4 text-sm">
                      <span className="text-[#6B7280]">{item.label}</span>
                      <span className="font-semibold tabular-nums text-[#111827]">
                        {item.value}
                        {item.suffix}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
              <Descriptions
                column={1}
                data={[
                  { label: t('tt.models.vendor'), value: detail.vendor_name || '-' },
                  { label: t('tt.models.billingType'), value: detail.quota_type === 1 ? t('tt.models.perUse') : t('tt.models.perToken') },
                  { label: t('tt.models.groupRatio'), value: `${price.usedGroup} ×${price.usedGroupRatio}` },
                  { label: t('tt.models.enabledGroups'), value: (detail.enable_groups || []).length ? <div className="flex flex-wrap">{(detail.enable_groups || []).map(renderGroupTag)}</div> : '-' },
                  { label: t('tt.models.tags'), value: renderModelFeatureTags(detail.tags) },
                  {
                    label: t('tt.models.endpoints'),
                    value: (detail.supported_endpoint_types || []).length
                      ? (
                          <div className="flex flex-wrap gap-1">
                            {(detail.supported_endpoint_types || []).map((item) => (
                              <Pill key={item} variant="neutral">
                                {endpointLabel(item, normalized.supportedEndpointMap)}
                              </Pill>
                            ))}
                          </div>
                        )
                      : '-',
                  },
                ]}
              />
            </div>
          )
        })()}
      </Drawer>
    </div>
  )
}
